import { useQuery } from '@apollo/react-hooks';
import OriginTracing from '@atlassiansox/origin-tracing';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { getAGGClient, isErrorMarkedAsHandled, markErrorAsHandled } from '@confluence/graphql';
import { useSessionData } from '@confluence/session-data';

import { DirectBuyWhileTrialingQuery } from '../graphql/DirectBuyWhileTrialingQuery.agggraphql';

/*
Determine a user's ability to downgrade to Confluence Standard and pay in one step
- userCanPayAndDowngrade: boolean value determining if user can downgrade and pay
- changeOfferingAndPayLink: Link to the downgrade to standard and pay experience
*/
export const useDowngradePaymentLink = (skip: boolean) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const PRODUCT_KEY = 'confluence.ondemand';
	const STANDARD_OFFERING_KEY = '2eda1119-fab4-4699-ab37-ff8f12052cde';

	const { cloudId } = useSessionData();
	const origin = new OriginTracing({ product: 'confluence' });

	const { data, loading, error } = useQuery(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		DirectBuyWhileTrialingQuery,
		{
			client: getAGGClient(),
			variables: {
				cloudId,
				productKey: PRODUCT_KEY,
				offeringKey: STANDARD_OFFERING_KEY,
			},
			skip,
		},
	);

	if (error && !isErrorMarkedAsHandled(error)) {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'failed',
				actionSubject: 'useDowngradePaymentLink',
				source: 'RecommendStandardOverPremiumModal',
				attributes: {
					growthFeature: 'RecommendStandardOverPremiumModal',
					error: error?.message,
				},
			},
		}).fire();
		markErrorAsHandled(error);
	}

	const entitlement = data?.tenantContexts?.[0]?.entitlementInfo?.entitlement;
	const changeOffering = entitlement?.experienceCapabilities?.changeOfferingV2;
	const configurePayment =
		entitlement?.subscription?.accountDetails?.invoiceGroup?.experienceCapabilities
			?.configurePaymentV2;
	const invoiceGroup = entitlement?.subscription?.accountDetails?.invoiceGroup;
	const hasPaymentDetails = invoiceGroup?.invoiceable;

	return {
		userCanPayAndDowngrade:
			changeOffering?.isAvailableToUser && configurePayment?.isAvailableToUser,
		changeOfferingAndPayLink:
			changeOffering?.experienceUrl && origin.addToUrl(changeOffering?.experienceUrl),
		hasPaymentDetails,
		error,
		loading,
	};
};
